import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { LocationBoxPlacement } from "../LocationLayout/types";
import { paintLocationParts } from "../LocationPartLabels/labels";
import { partLabelOffsetsForStack } from "../LocationPartLabels/stackPartLabels";
import { partLabelsWithoutOwnStacks } from "../LocationPartLabels/filter";
import {
  type LocationRun,
} from "../LocationRun/runs";
import type { ActiveLocationGroup } from "../ActiveLocationGroup";
import type { OnStageLayoutAdjust } from "../ActiveLocationGroup/layout";
import {
  isLocationRunVisible,
  locationStackZIndex,
} from "./markerGroup";

export function paintLocationBoxElements(
  root: HTMLElement,
  layout: BookTimelineLayout,
  playheadMs: number,
  placements: LocationBoxPlacement[],
  runsById: Map<string, LocationRun>,
  visibility: Map<string, boolean>,
  stageByRunId: Map<string, { anchorX: number; topY: number }>,
  onStageLayout: Map<string, OnStageLayoutAdjust>,
  locationGroups: readonly ActiveLocationGroup[],
  activePath: string[],
  activeRunId: string | undefined,
): void {
  const byRunId = new Map(placements.map((p) => [p.runId, p]));
  for (const el of root.querySelectorAll<HTMLElement>("[data-location-run]")) {
    const runId = el.dataset.locationRun ?? "";
    const placement = byRunId.get(runId);
    if (placement === undefined) {
      el.style.display = "none";
      continue;
    }
    const run = runsById.get(runId);
    if (run === undefined) {
      el.style.display = "none";
      continue;
    }
    const show = isLocationRunVisible(visibility, run.runId);
    const stage = stageByRunId.get(runId);
    if (!show || stage === undefined) {
      el.style.display = "none";
      el.style.opacity = "";
      el.setAttribute("aria-hidden", "true");
      continue;
    }
    el.style.display = "";
    el.style.visibility = "visible";
    el.style.opacity = "1";
    el.removeAttribute("aria-hidden");
    el.style.zIndex = String(
      locationStackZIndex(layout, run, playheadMs, placement.zIndex),
    );
    const onStage = onStageLayout.get(runId);
    const anchorX = onStage?.anchorX ?? stage.anchorX;
    const topY = stage.topY + (onStage?.topYDelta ?? 0);
    const stackAbove = onStage === undefined && placement.above;
    const shiftY = stackAbove ? "-100%" : "0";
    el.style.transform =
      `translate(${anchorX}px, ${topY}px) `
      + `translate(-50%, ${shiftY})`;
    const nested = partLabelsWithoutOwnStacks(
      locationGroups,
      layout,
      partLabelOffsetsForStack(
        run,
        layout,
        playheadMs,
        activePath,
        activeRunId,
      ),
    );
    paintLocationParts(el, nested);
    const site = el.querySelector<HTMLElement>(
      ".timeline-location-box:not(.timeline-location-box--nested)",
    );
    if (site !== null) {
      site.style.transform = "";
    }
  }
}
